// API Endpoint Block - Specialized block for API documentation
class ApiEndpointBlock {
  static get toolbox() {
    return {
      title: 'API Endpoint',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9H3m9 9a9 9 0 0 1-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 0 1 9-9"></path></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.data = {
      method: data.method || 'GET',
      url: data.url || '/api/endpoint',
      description: data.description || '',
      auth: data.auth || false,
      deprecated: data.deprecated || false
    };
    this.config = config;
    this.api = api;
    this.readOnly = readOnly;
    this.wrapper = null;
    this.methods = config.methods || ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('api-endpoint-block');
    this._renderEndpoint();
    return this.wrapper;
  }

  _getMethodColor(method) {
    const colors = {
      GET: 'bg-emerald-500 text-white',
      POST: 'bg-blue-500 text-white',
      PUT: 'bg-amber-500 text-white',
      PATCH: 'bg-purple-500 text-white',
      DELETE: 'bg-red-500 text-white'
    };
    return colors[method] || 'bg-slate-500 text-white';
  }

  _renderEndpoint() {
    const methodColor = this._getMethodColor(this.data.method);

    this.wrapper.innerHTML = `
      <div class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden ${this.data.deprecated ? 'opacity-60' : ''}" data-testid="api-endpoint-block">
        <div class="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800">
          ${!this.readOnly ? `
            <select class="method-select px-3 py-1.5 text-xs font-bold rounded ${methodColor} border-0 cursor-pointer">
              ${this.methods.map(m => `
                <option value="${m}" ${this.data.method === m ? 'selected' : ''} class="bg-white text-slate-900">${m}</option>
              `).join('')}
            </select>
          ` : `
            <span class="px-3 py-1.5 text-xs font-bold rounded ${methodColor}">${this.data.method}</span>
          `}
          
          ${!this.readOnly ? `
            <input 
              type="text"
              class="url-input flex-1 px-3 py-1.5 text-sm font-mono bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="/api/endpoint"
              value="${this.data.url}"
            />
          ` : `
            <code class="flex-1 px-3 py-1.5 text-sm font-mono bg-slate-100 dark:bg-slate-900 rounded text-slate-700 dark:text-slate-300">${this.data.url}</code>
          `}
          
          <div class="flex items-center gap-2">
            ${this.data.auth ? `
              <span class="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                Auth
              </span>
            ` : ''}
            ${this.data.deprecated ? `
              <span class="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                Deprecated
              </span>
            ` : ''}
            <button class="copy-btn p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" title="Copy URL">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            </button>
          </div>
        </div>
        
        <div class="px-4 py-3 bg-white dark:bg-slate-900">
          ${!this.readOnly ? `
            <textarea 
              class="description-input w-full min-h-[60px] p-0 text-sm text-slate-600 dark:text-slate-400 bg-transparent border-0 resize-none focus:outline-none"
              placeholder="Describe what this endpoint does..."
            >${this.data.description}</textarea>
            <div class="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <label class="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                <input type="checkbox" class="auth-checkbox rounded" ${this.data.auth ? 'checked' : ''} />
                Requires Authentication
              </label>
              <label class="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                <input type="checkbox" class="deprecated-checkbox rounded" ${this.data.deprecated ? 'checked' : ''} />
                Deprecated
              </label>
            </div>
          ` : this.data.description ? `
            <p class="text-sm text-slate-600 dark:text-slate-400">${this.data.description}</p>
          ` : `
            <p class="text-sm text-slate-400 italic">No description</p>
          `}
        </div>
      </div>
    `;

    // Event listeners
    if (!this.readOnly) {
      const methodSelect = this.wrapper.querySelector('.method-select');
      const urlInput = this.wrapper.querySelector('.url-input');
      const descriptionInput = this.wrapper.querySelector('.description-input');
      const authCheckbox = this.wrapper.querySelector('.auth-checkbox');
      const deprecatedCheckbox = this.wrapper.querySelector('.deprecated-checkbox');

      if (methodSelect) {
        methodSelect.addEventListener('change', (e) => {
          this.data.method = e.target.value;
          this._renderEndpoint();
        });
      }

      if (urlInput) {
        urlInput.addEventListener('input', (e) => {
          this.data.url = e.target.value;
        });
      }

      if (descriptionInput) {
        descriptionInput.addEventListener('input', (e) => {
          this.data.description = e.target.value;
        });
      }

      if (authCheckbox) {
        authCheckbox.addEventListener('change', (e) => {
          this.data.auth = e.target.checked;
          this._renderEndpoint();
        });
      }

      if (deprecatedCheckbox) {
        deprecatedCheckbox.addEventListener('change', (e) => {
          this.data.deprecated = e.target.checked;
          this._renderEndpoint();
        });
      }
    }

    // Copy button
    const copyBtn = this.wrapper.querySelector('.copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(this.data.url);
        copyBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        setTimeout(() => {
          copyBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
        }, 2000);
      });
    }
  }

  save() {
    return {
      method: this.data.method,
      url: this.data.url,
      description: this.data.description,
      auth: this.data.auth,
      deprecated: this.data.deprecated
    };
  }

  static get sanitize() {
    return {
      method: false,
      url: false,
      description: true,
      auth: false,
      deprecated: false
    };
  }
}

export default ApiEndpointBlock;
